import { friendlyImpactTitle } from '@upright/common/utils';
import { valenceColor } from '@upright/ui/common/generic/utilities/format';
import { zeroWidthNonBreakingSpace } from '@upright/ui/common/generic/utilities/zero-width-space';
import { InfoBoxBottomPart } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-bottom-part';
import { InfoBoxLikeButton } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-like-button';
import { InfoBoxLinkButton } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-link-button';
import { InfoBoxQuoteText } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-quote-text';
import { InfoBoxStepper } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-stepper';
import { InfoBoxTitleRow } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-title-row';
import { InsightOrTargetQuote } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/styles';
import { ThreePartInfoBox } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/three-part-info-box';
import { useState } from 'react';
import styled from 'styled-components';
import { CompanySBTIDataType, ImpactTargetOrSbtiTarget } from '../common/types';
import { ScienceBasedTargetInfoBox } from './science-based-target-info-box.tsx';

interface Props {
  id: string;
  targets: ImpactTargetOrSbtiTarget[];
  x: number;
  y: number;
  width: number;
  height: number;
  entityName?: string;
}

export function TargetInfoBox(props: Props) {
  const targets = props.targets.sort(d => d.order || 0); // Lowest order will be shown first
  const [targetIndex, setTargetIndex] = useState(0);
  const target = targets[targetIndex];

  if (target.clientDataType === CompanySBTIDataType.SBTI_TARGET) {
    return (
      <ScienceBasedTargetInfoBox
        targets={targets}
        target={target}
        width={props.width}
        height={props.height}
        x={props.x}
        y={props.y}
        id={props.id}
        targetIndex={targetIndex}
        setTargetIndex={setTargetIndex}
        entityName={props.entityName}
      />
    );
  }

  const { text, link } = target;

  return (
    <ThreePartInfoBox
      width={props.width}
      height={props.height}
      x={props.x}
      y={props.y}
      id={props.id}
      contentTop={
        <>
          <ColorBlock style={{ backgroundColor: valenceColor(target.valence) }}>{zeroWidthNonBreakingSpace}</ColorBlock>
          <InfoBoxTitleRow>Targets on {friendlyImpactTitle(target.impactCategoryId)}</InfoBoxTitleRow>
        </>
      }
      contentMiddle={
        <InsightOrTargetQuote>
          <div>
            <InfoBoxQuoteText>{text}</InfoBoxQuoteText>
          </div>
        </InsightOrTargetQuote>
      }
      contentBottom={
        <>
          <InfoBoxBottomPart
            left={
              <>
                <InfoBoxLikeButton
                  companyId={target.companyId}
                  clientDataType={target.clientDataType}
                  clientDataId={target.id}
                  likes={target.likes}
                  liked={target.liked}
                />
                {link && <InfoBoxLinkButton href={link} />}
              </>
            }
            right={<InfoBoxStepper total={targets.length} setIndex={setTargetIndex} index={targetIndex} />}
          />
        </>
      }
    />
  );
}

const ColorBlock = styled.div`
  height: 2px;
`;
